import React, { useEffect, lazy } from 'react';
import { RouteComponentProps, useHistory } from 'react-router-dom';
import { CGProduct } from '../../types/shopify';
import HelmetHelper from 'components/Helmet/HelmetHelper';
import LoadingScreen from 'components/Progress/LoadingScreen';
import useNotification from 'effects/useNotification';
import FloatingNavigationMenu from 'components/Navigation/FloatingNavigationMenu';
import { useCollection } from 'effects/useCollection';
import debug from 'debug';
import useStickyMenu from 'effects/useStickyMenu';
import useTransparentMenu from 'effects/useTransparentMenu';
import usePhatHeader from 'effects/usePhatHeader';
import DynamicCarousel from 'components/DynamicCarousel/DynamicCarousel';
import { productToSlideItem } from 'components/DynamicCarousel/libDynamicCarousel';
const d = debug('dynamicCollectionScreen');

const DescriptionHelper = lazy(() => import('components/Layout/DescriptionHelper' /* webpackChunkName: "descriptionHelper" */));

interface MatchParams {
  collectionHandle: string;
}

interface CollectionScreenProps extends RouteComponentProps<MatchParams> {}

const DynamicCollectionScreen: React.FC<CollectionScreenProps> = (props) => {
  const collectionHandle = props.match.params.collectionHandle;
  useStickyMenu(false); // Make content fall behind mene...
  useTransparentMenu(true); // Make menu transparent for first 60 pixels...
  usePhatHeader(collectionHandle === 'corporate'); // Use the phat header (100px vs 60px)
  const showMessage = useNotification();
  const { collection, error: collectionError } = useCollection(collectionHandle);
  const history = useHistory();

  useEffect(() => {
    if (collectionError && collectionError?.message === '404') {
      // redirect to 404...
      return history.replace('/404');
    }

    if (collectionError) {
      showMessage(collectionError.message, 'error');
    }
  }, [showMessage, collectionError, history]);

  if (!collection) return <LoadingScreen />;
  d('renderCollection', collection, collection?.image?.src);

  // Lets display all products in the carousel...
  let carouselProducts = collection.products;
  // If there is an highlightProducts - lets filter the list of products...
  if (collection && collection.highlightProducts && collection.highlightProducts.handles) {
    // example: {"handles": ["jardan","restaurant-card","braun"], "variant":"hero"}
    const handlesToDisplay = collection.highlightProducts.handles;
    carouselProducts = collection.products.filter((product: CGProduct) => handlesToDisplay.indexOf(product.handle) >= 0);
  }

  return (
    <div>
      {collectionHandle === 'corporate' && <FloatingNavigationMenu />}
      <HelmetHelper
        //
        title={collection.seo?.title || collection.title}
        description={collection.seo?.description || collection.description}
        canonical={`/landing/${collection.handle}/`}
      />
      {collectionHandle !== 'corporate' && (
        <>
          <DynamicCarousel
            displayMode="product"
            id={collection.handle}
            content={{
              title: collection.title,
              subtitle: collection.pd?.abstract,
              backgroundImage: collection?.image?.src,
              slides: carouselProducts.map((product, idx) =>
                productToSlideItem(
                  product,
                  {},
                  {
                    title: collection.title, // Override the product title
                    subtitle: collection.pd?.abstract, // Override the product abstract
                  },
                ),
              ),
            }}
          />
          <div style={{ height: 36 }} />
        </>
      )}
      <DescriptionHelper
        html={collection.descriptionHtml}
        data={{
          products: carouselProducts,
          backgroundImage: collection?.image?.src,
          title: collection.title,
        }}
      />
    </div>
  );
};

export default DynamicCollectionScreen;
